import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '../useFetch'
import { useAuth } from '../../contexts/AuthContext'

import { CONFIG } from '../../config'
import { BlockModel } from '../../types/court/court'
import { ApiError } from '../../types/exception/errorStatus'
import { getErrorMessage } from '../../utils/exception/exceptionUtils'
import { formatDateTimesInText } from '../../utils/dateUtils'

export function useCreateBlock() {
  const { t, i18n } = useTranslation(['blocks', 'exceptions'])

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const { authInfo } = useAuth()
  const { poster } = useFetch(authInfo?.accessToken, authInfo?.refreshToken)

  const resetState = () => {
    setLoading(false)
    setError(null)
    setSuccess(null)
  }

  const createBlock = async (
    request: BlockModel,
    /** Confirms cancelling overlapping bookings (with the block's reason). */
    cancelConflicts = false
  ) => {
    setLoading(true)
    try {
      const query = cancelConflicts ? '?cancelConflicts=true' : ''
      // One request blocks every selected court, all or nothing.
      const data = await poster<BlockModel[]>(
        `${CONFIG.BOUNCE_API}/api/v1/blocks${query}`,
        {
          courtIds: request.courtIds?.length ? request.courtIds : [request.courtId],
          startTime: request.startTime,
          endTime: request.endTime,
          reason: request.reason,
          notes: request.notes,
        },
        'application/json'
      )
      if (data) {
        setSuccess(t('blocks:blocks.successfully-created'))
        setError(null)
        return data
      }
    } catch (error) {
      console.log(error)
      if (error instanceof ApiError) {
        const translatedMessage = formatDateTimesInText(
          getErrorMessage(error.data),
          i18n.language
        )
        setError(translatedMessage)
      } else {
        setError(t('exceptions:unknown'))
      }
    } finally {
      setLoading(false)
    }
  }

  return {
    createBlock,
    loading,
    error,
    success,
    resetState,
  }
}
